import { zodResolver } from '@hookform/resolvers/zod';
import { useContext } from 'react';
import { useFormContext, UseFormProps, useForm as useRHForm } from 'react-hook-form';
import { z } from 'zod';

import { FormFieldContext, FormItemContext } from '../context';
import { UseFormFieldProps } from '../type';

const getZodSchemaAtPath = (schema: z.ZodTypeAny | undefined, path: string): z.ZodTypeAny | undefined => {
  if (!schema || !(schema instanceof z.ZodObject)) return undefined;

  const pathSegments = path.replace(/\[(\d+)\]/g, '.$1').split('.');

  let current: z.ZodTypeAny | undefined = schema;

  for (const segment of pathSegments) {
    if (!current) return undefined;

    if (current instanceof z.ZodObject) {
      current = (current.shape as Record<string, z.ZodTypeAny> | undefined)?.[segment];
    } else if (current instanceof z.ZodArray) {
      current = current.element as z.ZodTypeAny;
    } else {
      return undefined;
    }
  }

  return current;
};

const isFieldRequired = (fieldSchema: z.ZodTypeAny | undefined): boolean => {
  if (!fieldSchema) return false;

  return !fieldSchema.safeParse(undefined).success;
};

const useFormField = <P extends UseFormFieldProps & { label?: string }>(props: P) => {
  const { label, name, forceRequired, isRequired, subLabel, subLabelClassName, link, ...otherProps } = props;
  const id = name;

  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(name, formState);

  return {
    formFieldProps: {
      id,
      name,
      label,
      subLabel,
      subLabelClassName,
      link,
      forceRequired: typeof forceRequired === 'boolean' ? forceRequired : isRequired,
    },
    childProps: { ...otherProps, id, name },
    ...fieldState,
  };
};

const useFieldState = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();
  const _schema = (useFormContext() as unknown as typeof useFormContext & { _schema?: z.ZodSchema })._schema;

  const fieldState = getFieldState(fieldContext.name, formState);
  const fieldSchema = getZodSchemaAtPath(_schema, fieldContext.name);
  const schemaRequired = isFieldRequired(fieldSchema);
  const isRequired = typeof fieldContext.forceRequired === 'boolean' ? fieldContext.forceRequired : schemaRequired;

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }
  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    isRequired,
    ...fieldState,
  };
};

interface UseZodFormProps<S extends z.ZodSchema> extends Exclude<UseFormProps<z.infer<S>>, 'resolver'> {
  schema: S;
}

const useForm = <S extends z.ZodSchema>({ schema, ...formConfigs }: UseZodFormProps<S>) => {
  const methods = useRHForm<z.infer<S>>({
    ...formConfigs,
    resolver: zodResolver(schema),
  });

  (methods as typeof methods & { _schema: S })._schema = schema;

  return methods;
};

export { isFieldRequired, useFieldState, useForm, useFormField };
